import { Stack, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Trans } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';

import {
  confirmAdultPickupRequest,
  createNewOperationRequest,
} from '@/api/operation';

import { useConfirmation } from '@/context';

import { selectLoggedInUser } from '@/redux/auth/selectors';
import { selectRightStartFeature } from '@/redux/configs';
import { selectUserHolderJointMinor } from '@/redux/user-data';

import { Holder } from '@/types/api/operation';
import { HolderType, User } from '@/types/user';

import { getFullName } from '@/utils/user';

import { useAppSelector } from './redux';
import { useTranslate } from './useTranslate';

export function useConfirmAdultPickup() {
  const { t } = useTranslate();
  const enabledRightStartAccount = useAppSelector(selectRightStartFeature);
  const minorHolder = useAppSelector(selectUserHolderJointMinor);
  const { showConfirmation } = useConfirmation();
  const user = useAppSelector(selectLoggedInUser);
  const navigate = useNavigate();
  const location = useLocation();

  const {
    mutate: confirmAdultPickupMutate,
    isPending: isConfirmAdultPickupPending,
  } = useMutation({
    mutationKey: ['fetch-user-adult-pickup-status'],
    mutationFn: confirmAdultPickupRequest,
    onSuccess: () => {
      navigate(location.pathname, { replace: true });
    },
  });

  const {
    mutate: createNewOperationMutate,
    isPending: isCreateNewOperationPending,
  } = useMutation({
    mutationFn: createNewOperationRequest,
    onSuccess: () => {
      navigate(location.pathname, { replace: true });
    },
  });

  useEffect(() => {
    if (!user || !enabledRightStartAccount || !minorHolder) {
      return;
    }

    if (showConfirmationDialog(user)) {
      showConfirmation({
        content: <ModalContent minorHolder={minorHolder} />,
        secondaryText: t('modalConfirmPickup.button.secondary.text'),
        primaryText: t('modalConfirmPickup.button.primary.text'),
        primaryButtonProps: {
          variant: 'outlined',
        },
        secondaryButtonProps: {
          variant: 'contained',
          sx: {
            minWidth: '120px !important',
            maxWidth: '240px !important',
          },
        },
        containerProps: {
          sx: {
            width: 400,
          },
        },
        showHeader: false,
        primaryAction: () => {
          createNewOperationMutate();
        },
        secondaryAction: () => {
          confirmAdultPickupMutate({
            operationId: user.operationId!,
          });
        },
      });
    }
  }, [
    confirmAdultPickupMutate,
    showConfirmation,
    user,
    enabledRightStartAccount,
    createNewOperationMutate,
    minorHolder,
    t,
  ]);

  return {
    isPending: isConfirmAdultPickupPending || isCreateNewOperationPending,
  };
}

function showConfirmationDialog(user?: User) {
  return !user?.adultPickup && user?.holderType === HolderType.JointAdult;
}

function ModalContent({ minorHolder }: { minorHolder: Holder }) {
  const { t, language } = useTranslate();

  return (
    <Stack
      spacing={1}
      direction="column"
      alignItems="center"
      textAlign="center"
      mb={2}
    >
      <Typography variant="h3">
        {t('modalConfirmPickup.content.title')}
      </Typography>
      <Typography mb={2} variant="body2">
        <Trans
          lang={language}
          i18nKey="modalConfirmPickup.content.text1"
          values={{
            childName: getFullName({
              firstName: minorHolder.firstName,
              middleName: minorHolder.middleName,
              lastName: minorHolder.lastName,
            }),
          }}
          components={{
            tag: <strong />,
          }}
        />
      </Typography>
      <Typography variant="body2">
        {t('modalConfirmPickup.content.text2')}
      </Typography>
    </Stack>
  );
}
